import React, { useState } from "react";
import { inviteStudentByEmail } from "../../services/authService";

// Modale d'invitation d'un nouveau client par email. Le coach saisit l'email
// (obligatoire) + un message personnel optionnel. Un lien magique est envoyé
// au prospect qui l'accepte via /invite?code=<code>.
//
// États UI :
//   - form     : saisie email + message
//   - sending  : requête en cours
//   - success  : invitation envoyée (affiche récap + option de copier le lien)
//   - error    : erreur d'envoi (affiché sous le form, garde la saisie)
export default function InviteStudentModal({ onClose, onSent }) {
	const [email, setEmail] = useState("");
	const [message, setMessage] = useState("");
	const [state, setState] = useState("form"); // form | sending | success
	const [error, setError] = useState("");
	const [result, setResult] = useState(null); // { code, inviteUrl, existingUser }
	const [copied, setCopied] = useState(false);

	const emailIsValid = (() => {
		const trimmed = email.trim();
		if (!trimmed) return false;
		// Validation minimaliste : présence de @ et d'un . après. Le backend re-check.
		const at = trimmed.indexOf("@");
		return at > 0 && trimmed.indexOf(".", at) > at + 1 && trimmed.length >= 6;
	})();

	const handleSend = async () => {
		if (!emailIsValid) return;
		setState("sending");
		setError("");
		const res = await inviteStudentByEmail(email.trim(), message.trim());
		if (res.success) {
			setResult(res);
			setState("success");
			onSent?.();
		} else {
			setError(res.error || "Erreur inattendue");
			setState("form");
		}
	};

	const handleCopyLink = async () => {
		if (!result?.inviteUrl) return;
		try {
			await navigator.clipboard.writeText(result.inviteUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (_) {}
	};

	const handleReset = () => {
		setEmail("");
		setMessage("");
		setResult(null);
		setError("");
		setCopied(false);
		setState("form");
	};

	return (
		<div className="mens-modal-overlay" onClick={state === "sending" ? undefined : onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
				<div className="mens-modal-header">
					<h3>Inviter un nouveau client</h3>
					<button className="mens-modal-close" onClick={onClose} disabled={state === "sending"}>×</button>
				</div>

				{state !== "success" && (
					<>
						<p style={{ fontSize: 13, color: "var(--ink-soft)", margin: "6px 0 16px 0", lineHeight: 1.5 }}>
							Envoyez un lien magique à votre client. Il pourra créer son compte (ou se connecter) et
							accepter l'invitation en un clic.
						</p>

						<div style={{ marginBottom: 12 }}>
							<label htmlFor="invite-email" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>
								Email du client
							</label>
							<input
								id="invite-email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="prenom.nom@exemple.com"
								className="mens-modal-input"
								autoFocus
								disabled={state === "sending"}
								onKeyDown={(e) => { if (e.key === "Enter" && emailIsValid) handleSend(); }}
							/>
						</div>

						<div style={{ marginBottom: 12 }}>
							<label htmlFor="invite-message" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>
								Message personnel <span style={{ fontWeight: 400, color: "var(--ink-faint)" }}>(optionnel)</span>
							</label>
							<textarea
								id="invite-message"
								value={message}
								onChange={(e) => setMessage(e.target.value.slice(0, 400))}
								placeholder="Ex : Salut Marc, comme convenu on suit ton programme via cette app."
								className="mens-modal-input"
								rows={3}
								disabled={state === "sending"}
								style={{ resize: "vertical" }}
							/>
							<div style={{ fontSize: 11, color: "var(--ink-faint)", textAlign: "right", marginTop: 2 }}>
								{message.length} / 400
							</div>
						</div>

						{error && (
							<div className="mens-modal-error" style={{ marginBottom: 8 }}>{error}</div>
						)}

						<div className="mens-modal-actions">
							<button className="mens-btn ghost" onClick={onClose} disabled={state === "sending"}>Annuler</button>
							<button
								className="mens-btn primary"
								onClick={handleSend}
								disabled={!emailIsValid || state === "sending"}
							>
								{state === "sending" ? "Envoi…" : "Envoyer l'invitation"}
							</button>
						</div>
					</>
				)}

				{state === "success" && result && (
					<>
						<div className="invite-success">
							<div className="invite-success-icon">
								<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
									<polyline points="22 4 12 14.01 9 11.01" />
								</svg>
							</div>
							<h4 className="invite-success-title">Invitation envoyée</h4>
							<p className="invite-success-msg">
								Un email a été envoyé à <strong>{email.trim()}</strong>.<br />
								{result.existingUser
									? "Le client a déjà un compte GymNote — il pourra accepter directement."
									: "Le client peut créer son compte via le lien reçu."}
							</p>

							<div className="invite-link-block">
								<div className="invite-link-label">Ou partagez ce lien directement</div>
								<div className="invite-link-row">
									<input
										type="text"
										value={result.inviteUrl}
										readOnly
										className="mens-modal-input"
										onFocus={(e) => e.target.select()}
									/>
									<button className="mens-btn ghost" onClick={handleCopyLink} style={{ flexShrink: 0 }}>
										{copied ? "Copié ✓" : "Copier"}
									</button>
								</div>
							</div>
						</div>

						<div className="mens-modal-actions">
							<button className="mens-btn ghost" onClick={handleReset}>Inviter un autre client</button>
							<button className="mens-btn primary" onClick={onClose}>Fermer</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
